/* eslint-disable @typescript-eslint/no-misused-promises */
import {Link} from "react-router-dom";
import {z} from "zod";
import {SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Alert, Box, Grid, Paper, Stack, Typography} from "@mui/material";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {userResetPassword} from "../../services/authenticationService";
import {
    getResourceErrorMessage,
    isResourceFailed,
    isResourceLoading,
    isResourceSucceeded,
} from "../../utils/resourceUtils";
import {swallowException} from "../../utils/appUtils";
import {ActionButton} from "../base/ActionButton";
import {LabeledInput} from "../base/LabeledInput";
import {AppLayout} from "../AppLayout";
import {EmptyHeader} from "../EmptyHeader";
import {TextInput} from "../base/TextInput";
import {useTranslation} from "react-i18next";

export const PasswordRestorePage: React.FC = () => {
    const {t} = useTranslation();
    const {reset, resource, userResetPassword: resetPassword} = useResourceManagement({userResetPassword});
    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm<LoginSchema>({
        resolver: zodResolver(PASSWORD_RESTORE_SCHEMA),
    });

    const onSubmit: SubmitHandler<LoginSchema> = (data) => {
        reset();
        resetPassword(data.email).catch(swallowException);
    };

    return (
        <AppLayout header={<EmptyHeader />}>
            <Grid container component="main" display="flex" justifyContent="center" alignItems="center" height="100%">
                <Grid
                    item
                    component={Paper}
                    elevation={0}
                    square
                    sx={{
                        minWidth: "31.25rem",
                        width: "31.25rem",
                        borderRadius: "1rem",
                    }}
                >
                    {isResourceFailed(resource) && (
                        <Alert severity="error" sx={{paddingX: "2.5rem"}}>
                            {getResourceErrorMessage(resource)}
                        </Alert>
                    )}
                    {isResourceSucceeded(resource) && (
                        <Alert severity="success" sx={{paddingX: "2.5rem"}}>
                            {t("password_restore_page.form.success")}
                        </Alert>
                    )}
                    <Stack
                        direction="column"
                        justifyContent="center"
                        sx={{
                            paddingY: "2rem",
                            paddingX: "3rem",
                        }}
                    >
                        <Stack direction="column" alignItems="center">
                            <Typography component="h1" variant="h5">
                                {t("password_restore_page.title")}
                            </Typography>
                            <Box sx={{marginTop: "1rem", paddingX: "1.25rem", width: "100%"}}>
                                <form>
                                    <LabeledInput label={t("password_restore_page.form.email")}>
                                        <TextInput
                                            name="email"
                                            type="text"
                                            register={register}
                                            errors={errors}
                                            inputProps={{autoFocus: true, autoComplete: "username"}}
                                        />
                                    </LabeledInput>
                                    <Typography fontSize={14}>{t("password_restore_page.form.required")}</Typography>
                                    <ActionButton
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        icon="none"
                                        sx={{
                                            marginTop: "1.5rem",
                                            marginBottom: "1rem",
                                        }}
                                        onClick={handleSubmit(onSubmit)}
                                        disabled={isResourceLoading(resource)}
                                    >
                                        {t("password_restore_page.form.submit_button")}
                                    </ActionButton>
                                    <Typography textAlign="center">
                                        {t("password_restore_page.form.signin1")}{" "}
                                        <Link to="/login" style={{textDecoration: "none"}}>
                                            {t("password_restore_page.form.signin2")}
                                        </Link>
                                    </Typography>
                                </form>
                            </Box>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </AppLayout>
    );
};

const PASSWORD_RESTORE_SCHEMA = z.object({
    email: z.string().email(),
});

type LoginSchema = z.infer<typeof PASSWORD_RESTORE_SCHEMA>;
